import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Slider,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import SaveIcon from "@mui/icons-material/Save";
import {
  deleteDevicePreset,
  getDeviceOptionTypes,
  getDevicePresets,
  saveDevicePreset,
} from "../api/cleaning";

// Cleaning presets of one robot ("Quick", "Deep clean", ...), edited against the
// options that robot accepts (cleaning_option_types):
// - whole numbers (Lionsbot: brush height, suction, ...) -> slider
// - named values (Bosch: suction power Silent/Standard/Max, ...) -> buttons
// - on/off (Bosch: carpet boost, ...) -> On / Off buttons
// Every option can be left "not set": the zone's own setting (Lionsbot) or the
// robot's own app setting (Bosch) is used. The preset order here is the order of the
// slider on the controls page; the default preset is the one it opens on.

const NOT_SET = "__not_set__";

const prettyValue = (value) =>
  String(value)
    .replace(/_/g, " ")
    .replace(/^./, (c) => c.toUpperCase());

const sortPresets = (presets) =>
  [...presets].sort(
    (a, b) =>
      (a.sort_order ?? 0) - (b.sort_order ?? 0) ||
      (a.cleaning_level ?? 0) - (b.cleaning_level ?? 0),
  );

const newDraft = (presets) => ({
  pk_id: null,
  frontend_name: "",
  frontend_name_dk: "",
  is_default: presets.length === 0,
  active: true,
  values: {},
});

function OptionControl({ type, value, disabled, onChange }) {
  const isSet = value !== undefined && value !== null && value !== "";
  const notSetLabel = type.scope === "zone" ? "Zone setting" : "Robot setting";

  if (type.value_type === "int") {
    const min = Number(type.min_value ?? 0);
    const max = Number(type.max_value ?? 10);
    const step = Number(type.step_value ?? 1) || 1;
    const numeric = isSet ? Number(value) : Number(type.default_value ?? min);
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Tooltip title={isSet ? "Set by this preset" : notSetLabel}>
          <Checkbox
            size="small"
            checked={isSet}
            disabled={disabled}
            onChange={(e) =>
              onChange(e.target.checked ? String(numeric) : null)
            }
            sx={{ p: 0.5 }}
          />
        </Tooltip>
        <Slider
          size="small"
          value={Number.isFinite(numeric) ? numeric : min}
          min={min}
          max={max}
          step={step}
          marks
          valueLabelDisplay="auto"
          disabled={disabled || !isSet}
          onChange={(_, next) => onChange(String(next))}
          sx={{ flex: 1 }}
        />
      </Box>
    );
  }

  const choices =
    type.value_type === "boolean"
      ? [
          { value: "true", label: "On" },
          { value: "false", label: "Off" },
        ]
      : (type.allowed_values || []).map((v) => ({ value: v, label: prettyValue(v) }));

  return (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={isSet ? String(value) : NOT_SET}
      disabled={disabled}
      onChange={(_, next) => {
        if (next === null) return; // clicking the selected button keeps it
        onChange(next === NOT_SET ? null : next);
      }}
      sx={{ flexWrap: "wrap", "& .MuiToggleButton-root": { textTransform: "none", py: 0.25 } }}
    >
      <ToggleButton value={NOT_SET}>
        <Typography variant="caption" color="text.secondary">
          {notSetLabel}
        </Typography>
      </ToggleButton>
      {choices.map((choice) => (
        <ToggleButton key={choice.value} value={choice.value}>
          {choice.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}

function describeValue(type, value) {
  if (value === undefined || value === null || value === "") {
    return type.scope === "zone" ? "Zone setting" : "Robot setting";
  }
  if (type.value_type === "boolean") return value === "true" ? "On" : "Off";
  if (type.value_type === "enum") return prettyValue(value);
  return String(value);
}

export default function CleaningPresetsEditor({ deviceId, showSnackbar }) {
  const [optionTypes, setOptionTypes] = useState([]);
  const [deviceInfo, setDeviceInfo] = useState(null);
  const [presets, setPresets] = useState([]);
  const [drafts, setDrafts] = useState({}); // key: pk_id or "new" -> draft preset
  const [saving, setSaving] = useState({});
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const notify = useCallback(
    (message, severity = "success") => showSnackbar?.(message, severity),
    [showSnackbar],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [types, list] = await Promise.all([
        getDeviceOptionTypes(deviceId),
        getDevicePresets(deviceId, { activeOnly: false }),
      ]);
      setOptionTypes(types.option_types || []);
      setDeviceInfo(types.device || null);
      setPresets(sortPresets(list || []));
    } catch (err) {
      setLoadError(err.message);
    } finally {
      setLoading(false);
    }
  }, [deviceId]);

  useEffect(() => {
    load();
  }, [load]);

  const typesByKey = useMemo(
    () => new Map(optionTypes.map((type) => [type.option_key, type])),
    [optionTypes],
  );

  // Only options this robot accepts are saved (the API rejects others)
  const supportedValues = (values = {}) =>
    Object.fromEntries(
      Object.entries(values || {}).filter(([optionKey]) => typesByKey.has(optionKey)),
    );

  const draftKey = (preset) => (preset.pk_id ? String(preset.pk_id) : "new");

  const startEdit = (preset) =>
    setDrafts((prev) => ({
      ...prev,
      [draftKey(preset)]: JSON.parse(JSON.stringify(preset)),
    }));

  const cancelEdit = (key) =>
    setDrafts((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });

  const updateDraft = (key, changes) =>
    setDrafts((prev) => ({ ...prev, [key]: { ...prev[key], ...changes } }));

  const updateDraftValue = (key, optionKey, value) =>
    setDrafts((prev) => {
      const values = { ...(prev[key]?.values || {}) };
      if (value === null || value === undefined || value === "") {
        delete values[optionKey];
      } else {
        values[optionKey] = value;
      }
      return { ...prev, [key]: { ...prev[key], values } };
    });

  const isDirty = (key, original) =>
    !!drafts[key] && JSON.stringify(drafts[key]) !== JSON.stringify(original);

  const save = async (key) => {
    const draft = drafts[key];
    if (!draft?.frontend_name?.trim()) {
      notify("Give the preset a name", "warning");
      return;
    }
    setSaving((prev) => ({ ...prev, [key]: true }));
    try {
      await saveDevicePreset(deviceId, {
        pk_id: draft.pk_id || undefined,
        frontend_name: draft.frontend_name.trim(),
        frontend_name_dk: (draft.frontend_name_dk || "").trim(),
        sort_order:
          draft.sort_order ??
          (presets.length
            ? Math.max(...presets.map((p) => Number(p.sort_order) || 0)) + 1
            : 1),
        is_default: !!draft.is_default,
        active: draft.active !== false,
        values: supportedValues(draft.values),
      });
      notify(`Preset "${draft.frontend_name.trim()}" saved`);
      cancelEdit(key);
      await load();
    } catch (err) {
      notify(err.message, "error");
    } finally {
      setSaving((prev) => ({ ...prev, [key]: false }));
    }
  };

  const remove = async (preset) => {
    try {
      await deleteDevicePreset(deviceId, preset.pk_id);
      notify(`Preset "${preset.frontend_name}" deleted`);
      setConfirmDelete(null);
      cancelEdit(String(preset.pk_id));
      await load();
    } catch (err) {
      notify(err.message, "error");
    }
  };

  // Move a preset left/right in the controls-page slider (swaps sort order)
  const move = async (index, direction) => {
    const other = index + direction;
    if (other < 0 || other >= presets.length) return;
    const a = presets[index];
    const b = presets[other];
    const orderA = Number(a.sort_order) || index + 1;
    const orderB = Number(b.sort_order) || other + 1;
    const swapped = orderA === orderB ? [orderB + direction, orderA] : [orderB, orderA];
    try {
      await Promise.all([
        saveDevicePreset(deviceId, {
          ...a,
          values: supportedValues(a.values),
          sort_order: swapped[0],
        }),
        saveDevicePreset(deviceId, {
          ...b,
          values: supportedValues(b.values),
          sort_order: swapped[1],
        }),
      ]);
      await load();
    } catch (err) {
      notify(err.message, "error");
    }
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadError) {
    return <Alert severity="error">Failed to load cleaning presets: {loadError}</Alert>;
  }

  if (optionTypes.length === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography color="text.secondary">
          No cleaning options are defined for this robot
          {deviceInfo
            ? ` (${deviceInfo.manufacturer}${deviceInfo.model_code ? ` ${deviceInfo.model_code}` : ""})`
            : ""}
          . Add them to cleaning_option_types.
        </Typography>
      </Paper>
    );
  }

  const cards = [...presets];
  if (drafts.new) cards.push({ ...drafts.new, isNew: true });

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          The controls page shows these presets from left to right on its slider
          and opens on the default one. Options left as{" "}
          {optionTypes.some((t) => t.scope === "zone") ? "“Zone setting”" : "“Robot setting”"}{" "}
          are not changed by the preset.
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          disabled={!!drafts.new}
          onClick={() =>
            setDrafts((prev) => ({ ...prev, new: newDraft(presets) }))
          }
        >
          Add preset
        </Button>
      </Box>

      {cards.length === 0 && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography color="text.secondary">
            No presets yet. Without presets the controls page shows no cleaning
            slider and the robot cleans with its own settings.
          </Typography>
        </Paper>
      )}

      <Grid container spacing={3}>
        {cards.map((preset, index) => {
          const key = preset.isNew ? "new" : String(preset.pk_id);
          const draft = drafts[key];
          const isEditing = !!draft;
          const isSaving = !!saving[key];
          const shown = draft || preset;
          const dirty = preset.isNew || isDirty(key, preset);

          return (
            <Grid size={{ xs: 12, md: 6, lg: 4 }} key={key}>
              <Card
                variant="outlined"
                sx={{
                  height: "100%",
                  opacity: shown.active === false && !isEditing ? 0.6 : 1,
                  borderColor: isEditing ? "primary.main" : undefined,
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                      gap: 1,
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {shown.frontend_name || "New preset"}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      {dirty && isEditing && (
                        <Chip size="small" color="warning" label="Edited" />
                      )}
                      {shown.is_default && (
                        <Chip size="small" color="primary" label="Default" />
                      )}
                      {shown.active === false && (
                        <Chip size="small" label="Hidden" />
                      )}
                      {!preset.isNew && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`Level ${preset.cleaning_level}`}
                        />
                      )}
                    </Box>
                  </Box>
                  {!isEditing && shown.frontend_name_dk && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      DA: {shown.frontend_name_dk}
                    </Typography>
                  )}

                  <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
                    {!preset.isNew && !isEditing && (
                      <>
                        <Tooltip title="Move left on the slider">
                          <span>
                            <IconButton
                              size="small"
                              disabled={index === 0}
                              onClick={() => move(index, -1)}
                            >
                              <ArrowBackIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Move right on the slider">
                          <span>
                            <IconButton
                              size="small"
                              disabled={index >= presets.length - 1}
                              onClick={() => move(index, 1)}
                            >
                              <ArrowForwardIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </>
                    )}
                    {!preset.isNew && (
                      <IconButton
                        size="small"
                        color={isEditing ? "primary" : "default"}
                        onClick={() => (isEditing ? cancelEdit(key) : startEdit(preset))}
                      >
                        {isEditing ? (
                          <LockOpenOutlinedIcon fontSize="small" />
                        ) : (
                          <LockOutlinedIcon fontSize="small" />
                        )}
                      </IconButton>
                    )}
                  </Box>

                  {isEditing && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 2 }}>
                      <TextField
                        size="small"
                        label="Name (English)"
                        value={draft.frontend_name || ""}
                        onChange={(e) => updateDraft(key, { frontend_name: e.target.value })}
                        disabled={isSaving}
                      />
                      <TextField
                        size="small"
                        label="Name (Danish)"
                        value={draft.frontend_name_dk || ""}
                        onChange={(e) => updateDraft(key, { frontend_name_dk: e.target.value })}
                        disabled={isSaving}
                      />
                      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={!!draft.is_default}
                              onChange={(e) => updateDraft(key, { is_default: e.target.checked })}
                              disabled={isSaving}
                            />
                          }
                          label="Default"
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={draft.active !== false}
                              onChange={(e) => updateDraft(key, { active: e.target.checked })}
                              disabled={isSaving}
                            />
                          }
                          label="Shown on controls page"
                        />
                      </Box>
                      <Divider />
                    </Box>
                  )}

                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    {optionTypes.map((type) => (
                      <Box key={type.option_key}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "baseline",
                            mb: isEditing ? 0.5 : 0,
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {type.label}
                          </Typography>
                          <Typography
                            variant="caption"
                            color={
                              shown.values?.[type.option_key] != null
                                ? "text.primary"
                                : "text.secondary"
                            }
                          >
                            {describeValue(type, shown.values?.[type.option_key])}
                          </Typography>
                        </Box>
                        {isEditing && (
                          <OptionControl
                            type={type}
                            value={draft.values?.[type.option_key]}
                            disabled={isSaving}
                            onChange={(value) => updateDraftValue(key, type.option_key, value)}
                          />
                        )}
                      </Box>
                    ))}
                    {/* Values saved for options this robot no longer accepts */}
                    {Object.keys(shown.values || {})
                      .filter((optionKey) => !typesByKey.has(optionKey))
                      .map((optionKey) => (
                        <Typography key={optionKey} variant="caption" color="warning.main">
                          {optionKey} = {shown.values[optionKey]} (not supported, not sent)
                        </Typography>
                      ))}
                  </Box>

                  {isEditing && (
                    <Box
                      sx={{
                        mt: 2,
                        pt: 2,
                        borderTop: 1,
                        borderColor: "divider",
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 1,
                        flexWrap: "wrap",
                      }}
                    >
                      <Box>
                        {!preset.isNew &&
                          (confirmDelete === key ? (
                            <Button
                              size="small"
                              color="error"
                              variant="contained"
                              onClick={() => remove(preset)}
                            >
                              Confirm delete
                            </Button>
                          ) : (
                            <Button
                              size="small"
                              color="error"
                              startIcon={<DeleteOutlineIcon />}
                              onClick={() => setConfirmDelete(key)}
                              disabled={isSaving}
                            >
                              Delete
                            </Button>
                          ))}
                      </Box>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Button
                          size="small"
                          startIcon={<CloseIcon />}
                          onClick={() => {
                            setConfirmDelete(null);
                            cancelEdit(key);
                          }}
                          disabled={isSaving}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<SaveIcon />}
                          onClick={() => save(key)}
                          disabled={isSaving || (!dirty && !preset.isNew)}
                        >
                          {isSaving ? "Saving..." : "Save"}
                        </Button>
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </>
  );
}
